import { LogOutIcon, PencilIcon } from 'lucide-react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { DoctorSubstituteDocumentsCard } from '@/components/doctor-substitute-documents/doctor-substitute-documents-card';
import {
  AssistantProfileForm,
  AssistantProfileUseForm,
} from '@/components/forms/assistant-profile-form';
import {
  DoctorProfileForm,
  DoctorProfileUseForm,
} from '@/components/forms/doctor-profile-form';
import EditAdminProfileForm from '@/components/forms/edit-admin-profile/edit-admin-profile-form';
import { EditAdminProfileUseForm } from '@/components/forms/edit-admin-profile/use-edit-admin-profile-form';
import EditUserProfileForm from '@/components/forms/edit-user-profile/edit-user-profile-form';
import { EditUserProfileUseForm } from '@/components/forms/edit-user-profile/use-edit-user-profile-form';
import { RhythmDisplay } from '@/components/forms/rhythm-fields';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { Button } from '../../ui/button';
import { Separator } from '../../ui/separator';
import { selectSheetViewModel } from '../sheet.viewmodel';
import {
  hasUnifiedFormMode,
  selectViewProfileViewModel,
} from './view-profile-sheet.viewmodel';

/** Shown for a profile value that was never filled in. */
const EMPTY_VALUE = '—';

export const ViewProfileSheet = (data?: {
  onClose?: ({ success }: { success: boolean }) => void;
}) => {
  const { t } = useTranslation();
  const sheetViewModel = selectSheetViewModel({
    sheetName: 'ViewProfileSheet',
    api: {
      onClose: data?.onClose,
    },
  });

  const viewModel = selectViewProfileViewModel();
  const isMobile = useIsMobile();

  const informationNode: ReactNode = (() => {
    return (
      <div className="grid gap-3">
        <div className="flex justify-center">
          <Avatar className="h-[80px] w-[80px] rounded-md border">
            <AvatarImage
              src={viewModel.information.picture}
              alt="avatar"
            />
            <AvatarFallback className="rounded-md text-2xl">
              {viewModel.information.initials}
            </AvatarFallback>
          </Avatar>
        </div>

        <div className="font-semibold">
          {t('view-profile-sheet.information.title')}
        </div>
        <ul className="grid gap-3">
          <li className="flex items-center justify-between">
            <span>{t('view-profile-sheet.information.firstname')}</span>
            <span className="text-muted-foreground">
              {viewModel.information.firstname}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span>{t('view-profile-sheet.information.lastname')}</span>
            <span className="text-muted-foreground">
              {viewModel.information.lastname}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span>{t('view-profile-sheet.information.email')}</span>
            <span className="text-muted-foreground">
              {viewModel.information.email}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span>{t('view-profile-sheet.information.phone-number')}</span>
            <span className="text-muted-foreground">
              {viewModel.information.phoneNumber ?? '-'}
            </span>
          </li>
        </ul>
      </div>
    );
  })();

  const doctorSubstituteInformationNode: ReactNode = (() => {
    if (!viewModel.doctorSubstitute) {
      return null;
    }

    return (
      <div className="grid gap-3">
        <div className="font-semibold">
          {t('view-profile-sheet.doctor-substitute-information.title')}
        </div>
        <ul className="grid gap-3">
          <li className="flex items-center justify-between">
            <span>
              {t(
                'view-profile-sheet.doctor-substitute-information.date-of-birth',
              )}
            </span>
            <span className="text-muted-foreground">
              {viewModel.doctorSubstitute.dateOfBirth}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span className="flex-1">
              {t('view-profile-sheet.doctor-substitute-information.address')}
            </span>
            <span className="flex-1 text-right text-muted-foreground">
              {viewModel.doctorSubstitute.address}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span>
              {t(
                'view-profile-sheet.doctor-substitute-information.order-registration-number',
              )}
            </span>
            <span className="text-muted-foreground">
              {viewModel.doctorSubstitute.orderRegistrationNumber}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span>
              {t(
                'view-profile-sheet.doctor-substitute-information.rpps-number',
              )}
            </span>
            <span className="text-muted-foreground">
              {viewModel.doctorSubstitute.rppsNumber}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span>
              {t('view-profile-sheet.doctor-substitute-information.thesis')}
            </span>
            <span className="text-muted-foreground">
              {t(viewModel.doctorSubstitute.thesis)}
            </span>
          </li>
          {/* Structured Rhythm Display */}
          <li className="flex flex-col gap-2">
            <span className="font-medium">{t('rhythm-structured.title')}</span>
            <RhythmDisplay
              onSiteSlotsWithoutAssistant={
                viewModel.doctorSubstitute.onSiteSlotsWithoutAssistant
              }
              remoteSlotsWithoutAssistant={
                viewModel.doctorSubstitute.remoteSlotsWithoutAssistant
              }
              onSiteSlotsWithAssistant={
                viewModel.doctorSubstitute.onSiteSlotsWithAssistant
              }
              remoteSlotsWithAssistant={
                viewModel.doctorSubstitute.remoteSlotsWithAssistant
              }
            />
          </li>
          <li className="flex items-center justify-between">
            <span>
              {t('view-profile-sheet.doctor-substitute-information.iban')}
            </span>
            <span className="text-muted-foreground">
              {viewModel.doctorSubstitute.iban}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span>
              {t('view-profile-sheet.doctor-substitute-information.bic')}
            </span>
            <span className="text-muted-foreground">
              {viewModel.doctorSubstitute.bic}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span>
              {t('view-profile-sheet.doctor-substitute-information.signature')}
            </span>
            <span className="text-muted-foreground">
              <Avatar className="z-0 h-10 w-10 rounded-md border">
                <AvatarImage
                  src={viewModel.doctorSubstitute.signatureUrl}
                  alt="avatar"
                />
                <AvatarFallback className="rounded-md text-2xl" />
              </Avatar>
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span>{t('edit-user-profile-form.urssaf-number.label')}</span>
            <span className="text-muted-foreground">
              {viewModel.doctorSubstitute.urssafNumber ?? EMPTY_VALUE}
            </span>
          </li>
          {viewModel.doctorSubstitute.licence && (
            <>
              <li className="flex items-center justify-between">
                <span>{t('edit-user-profile-form.licence-number.label')}</span>
                <span className="text-muted-foreground">
                  {viewModel.doctorSubstitute.licence.number ?? EMPTY_VALUE}
                </span>
              </li>
              <li className="flex items-center justify-between">
                <span>
                  {t('edit-user-profile-form.licence-expires-at.label')}
                </span>
                <span className="text-muted-foreground">
                  {viewModel.doctorSubstitute.licence.expiresAt ?? EMPTY_VALUE}
                </span>
              </li>
              <li className="flex items-center justify-between">
                <span>{t('edit-user-profile-form.licence-council.label')}</span>
                <span className="text-muted-foreground">
                  {viewModel.doctorSubstitute.licence.council ?? EMPTY_VALUE}
                </span>
              </li>
            </>
          )}
        </ul>
        <Separator className="my-2" />
        <DoctorSubstituteDocumentsCard
          plain
          doctorSubstituteId={viewModel.doctorSubstitute.documents.id}
          thesis={viewModel.doctorSubstitute.documents.thesis}
          licenceDocument={viewModel.doctorSubstitute.documents.licenceDocument}
          rcpDocument={viewModel.doctorSubstitute.documents.rcpDocument}
          canView
          canEdit
        />
      </div>
    );
  })();

  const doctorAttendingInformationNode: ReactNode = (() => {
    if (!viewModel.doctorAttending) {
      return null;
    }

    return (
      <div className="grid gap-3">
        <div className="font-semibold">
          {t('view-profile-sheet.doctor-substitute-information.title')}
        </div>
        <ul className="grid gap-3">
          <li className="flex items-center justify-between">
            <span>
              {t(
                'view-profile-sheet.doctor-substitute-information.date-of-birth',
              )}
            </span>
            <span className="text-muted-foreground">
              {viewModel.doctorAttending.dateOfBirth ?? '-'}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span className="flex-1">
              {t('view-profile-sheet.doctor-substitute-information.address')}
            </span>
            <span className="flex-1 text-right text-muted-foreground">
              {viewModel.doctorAttending.address ?? '-'}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span>
              {t(
                'view-profile-sheet.doctor-substitute-information.order-registration-number',
              )}
            </span>
            <span className="text-muted-foreground">
              {viewModel.doctorAttending.orderRegistrationNumber}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span>
              {t(
                'view-profile-sheet.doctor-substitute-information.rpps-number',
              )}
            </span>
            <span className="text-muted-foreground">
              {viewModel.doctorAttending.rppsNumber ?? '-'}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span>
              {t('view-profile-sheet.doctor-substitute-information.thesis')}
            </span>
            <span className="text-muted-foreground">
              {t(viewModel.doctorAttending.thesis)}
            </span>
          </li>
          {/* Structured Rhythm Display */}
          <li className="flex flex-col gap-2">
            <span className="font-medium">{t('rhythm-structured.title')}</span>
            <RhythmDisplay
              onSiteSlotsWithoutAssistant={
                viewModel.doctorAttending.onSiteSlotsWithoutAssistant
              }
              remoteSlotsWithoutAssistant={
                viewModel.doctorAttending.remoteSlotsWithoutAssistant
              }
              onSiteSlotsWithAssistant={
                viewModel.doctorAttending.onSiteSlotsWithAssistant
              }
              remoteSlotsWithAssistant={
                viewModel.doctorAttending.remoteSlotsWithAssistant
              }
            />
          </li>
          <li className="flex items-center justify-between">
            <span>
              {t('view-profile-sheet.doctor-substitute-information.iban')}
            </span>
            <span className="text-muted-foreground">
              {viewModel.doctorAttending.iban ?? '-'}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span>
              {t('view-profile-sheet.doctor-substitute-information.bic')}
            </span>
            <span className="text-muted-foreground">
              {viewModel.doctorAttending.bic ?? '-'}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span>
              {t('view-profile-sheet.doctor-substitute-information.signature')}
            </span>
            <span className="text-muted-foreground">
              <Avatar className="z-0 h-10 w-10 rounded-md border">
                <AvatarImage
                  src={viewModel.doctorAttending.signatureUrl}
                  alt="avatar"
                />
                <AvatarFallback className="rounded-md text-2xl" />
              </Avatar>
            </span>
          </li>
        </ul>
      </div>
    );
  })();

  const medicalAssistantInformationNode: ReactNode = (() => {
    if (!viewModel.medicalAssistant) {
      return null;
    }

    return (
      <div className="grid gap-3">
        <div className="font-semibold">
          {t('view-profile-sheet.medical-assistant-information.title')}
        </div>
        <ul className="grid gap-3">
          <li className="flex items-center justify-between">
            <span>
              {t('view-profile-sheet.medical-assistant-information.phone')}
            </span>
            <span className="text-muted-foreground">
              {viewModel.medicalAssistant.phone ?? '-'}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span>
              {t(
                'view-profile-sheet.medical-assistant-information.date-of-birth',
              )}
            </span>
            <span className="text-muted-foreground">
              {viewModel.medicalAssistant.dateOfBirth || '-'}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span>
              {t(
                'view-profile-sheet.medical-assistant-information.birth-place',
              )}
            </span>
            <span className="text-muted-foreground">
              {viewModel.medicalAssistant.birthPlace ?? '-'}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span>
              {t(
                'view-profile-sheet.medical-assistant-information.nationality',
              )}
            </span>
            <span className="text-muted-foreground">
              {viewModel.medicalAssistant.nationality ?? '-'}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span>
              {t(
                'view-profile-sheet.medical-assistant-information.social-security-number',
              )}
            </span>
            <span className="text-muted-foreground">
              {viewModel.medicalAssistant.socialSecurityNumber ?? '-'}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span className="flex-1">
              {t('view-profile-sheet.medical-assistant-information.address')}
            </span>
            <span className="flex-1 text-right text-muted-foreground">
              {viewModel.medicalAssistant.address ?? '-'}
            </span>
          </li>
        </ul>
      </div>
    );
  })();

  // Determine which extra information section to show (mutually exclusive)
  const doctorInformationNode =
    doctorSubstituteInformationNode ?? doctorAttendingInformationNode;
  const extraInformationNode =
    doctorInformationNode ?? medicalAssistantInformationNode;

  const sheetContent: ReactNode = (() => {
    if (viewModel.editProfile.data.show) {
      let formNode: ReactNode;
      if (viewModel.isMedicalAssistant) {
        // Medical assistant self-service profile
        formNode = (
          <AssistantProfileForm
            form={viewModel.editProfile.form as AssistantProfileUseForm}
          />
        );
      } else if (!viewModel.hasDoctorProfile) {
        // Admin profile - no doctor data
        formNode = (
          <EditAdminProfileForm
            form={viewModel.editProfile.form as EditAdminProfileUseForm}
          />
        );
      } else if (hasUnifiedFormMode(viewModel.editProfile)) {
        // Doctor self-edit (substitute or attending) - use unified form
        const ownDocuments = viewModel.doctorSubstitute?.documents;
        formNode = (
          <DoctorProfileForm
            form={viewModel.editProfile.form as DoctorProfileUseForm}
            mode={viewModel.editProfile.mode}
            documents={
              ownDocuments
                ? {
                    doctorSubstituteId: ownDocuments.id,
                    licenceDocument: ownDocuments.licenceDocument,
                    rcpDocument: ownDocuments.rcpDocument,
                    canView: true,
                    canEdit: true,
                  }
                : undefined
            }
          />
        );
      } else {
        // Fallback - should not happen with current code
        formNode = (
          <EditUserProfileForm
            form={
              viewModel.editProfile.form as unknown as EditUserProfileUseForm
            }
          />
        );
      }

      return (
        <>
          {formNode}
          {
            <SheetFooter className="flex gap-2">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={viewModel.editProfile.api.cancel}
              >
                {t('btn.cancel')}
              </Button>

              <Button
                type="submit"
                className="flex-1"
                onClick={viewModel.editProfile.api.submit}
                loading={viewModel.editProfile.data.saving}
              >
                {t('btn.save')}
              </Button>
            </SheetFooter>
          }
        </>
      );
    }

    return (
      <>
        {informationNode}
        {!!extraInformationNode && <Separator className="my-2" />}
        {extraInformationNode}
      </>
    );
  })();

  return (
    <Sheet
      open={sheetViewModel.data.show}
      onOpenChange={sheetViewModel.api.onOpenChange}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col overflow-y-auto sm:w-[600px] sm:max-w-lg"
      >
        <SheetHeader>
          <SheetTitle className="mt-6">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold leading-none tracking-tight">
                {t('view-profile-sheet.title')}
              </h3>
              <div className="flex gap-1">
                {!viewModel.editProfile.data.show && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={viewModel.editProfile.api.open}
                  >
                    <PencilIcon />
                  </Button>
                )}
              </div>
            </div>
          </SheetTitle>
          <SheetDescription></SheetDescription>
        </SheetHeader>

        <div className="px-4 pb-4">{sheetContent}</div>

        {isMobile && (
          <Button
            variant="destructive"
            className="mx-4 mb-4 w-auto gap-2"
            onClick={viewModel.api.logout}
          >
            <LogOutIcon className="h-4 w-4" />
            {t('user-profile-menu.logout')}
          </Button>
        )}
      </SheetContent>
    </Sheet>
  );
};
